import React, { useState, useEffect } from 'react';
import { Collapse, Timeline, Typography, Row, Col, Spin, Tag, Empty, Card } from 'antd';
import { fetchData } from '../../service/dataServices';
import { getDateTimeDisplayText } from '../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { localizeField } from '../../utils/localization';

const { Panel } = Collapse;
const { Text } = Typography;

const getActionColor = (action) => {
    if (!action) return 'gray';
    switch (action.toLowerCase()) {
        case 'create':
            return 'green';
        case 'update':
            return 'blue';
        case 'delete':
            return 'red';
        default:
            return 'gray';
    }
};

const getActionLabel = (action, t) => {
    if (!action) return t('changeHistory.modified');
    const lower = action.toLowerCase();
    if (lower === 'create') return t('changeHistory.created');
    if (lower === 'update') return t('changeHistory.updated');
    if (lower === 'delete') return t('changeHistory.deleted');
    return action.charAt(0).toUpperCase() + action.slice(1);
};

const SYSTEM_FIELDS = ['created_time', 'form_id', 'user_id', 'object_id', 'record_id', 'inventory_number'];

export default function FormChangeHistory({ recordId, formId }) {
    const { t, i18n } = useTranslation();
    const [activeKey, setActiveKey] = useState([]);
    const [logs, setLogs] = useState([]);
    const [schema, setSchema] = useState(null);
    const [fieldMap, setFieldMap] = useState({});

    // Fetch schema for field name mapping
    fetchData(`/api/form-schema/${formId}`, (data) => {
        setSchema(data);
    }, undefined, !!formId);

    // Fetch audit logs
    const { isLoading } = fetchData(
        `/api/audit/logs/form`,
        (response) => {
            const logsData = response?.content || response || [];
            setLogs(logsData);
        },
        { recordId },
        !!recordId && activeKey.length > 0
    );

    // Build field ID -> { name, type, sectionTitle } mapping from schema
    useEffect(() => {
        if (!schema) return;
        const map = {};
        schema.sections?.forEach(section => {
            const sectionTitle = localizeField(section.title) || '';
            section.rows?.forEach(row => {
                row.cols?.forEach(col => {
                    if (col.field) {
                        map[col.field.id] = {
                            name: col.field.name,
                            type: col.field.type,
                            sectionTitle: sectionTitle,
                        };
                    }
                });
            });
        });
        setFieldMap(map);
    }, [schema, i18n.language]);

    function translated(val) {
        return val?.[i18n.language] || val?.['en'] || val;
    }

    const getFieldDisplayName = (fieldId) => {
        const field = fieldMap[fieldId];
        if (field) {
            return translated(field.name);
        }
        // Format system field names nicely
        const formatted = fieldId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return formatted;
    };

    const isSystemField = (fieldId) => SYSTEM_FIELDS.includes(fieldId);

    // Group changes by section
    const groupChangesBySection = (changes) => {
        if (!changes || changes.length === 0) return {};

        const grouped = {};
        const systemChanges = [];

        changes.forEach(change => {
            if (isSystemField(change.field)) {
                systemChanges.push(change);
                return;
            }
            const field = fieldMap[change.field];
            const sectionTitle = field?.sectionTitle || t('changeHistory.otherFields');
            if (!grouped[sectionTitle]) {
                grouped[sectionTitle] = [];
            }
            grouped[sectionTitle].push(change);
        });

        if (systemChanges.length > 0) {
            grouped[t('changeHistory.systemFields')] = systemChanges;
        }

        return grouped;
    };

    const renderChangeValue = (value, fieldId) => {
        if (value === null || value === undefined) {
            return <Text type="secondary" italic>{t('changeHistory.empty')}</Text>;
        }
        const field = fieldMap[fieldId];
        if (field?.type === 'boolean') {
            return value ? t('customForm.yes', 'Yes') : t('customForm.no', 'No');
        }
        if (field?.type === 'date') {
            return String(value);
        }
        return String(value);
    };

    return (
        <Row style={{ marginTop: 16 }}>
            <Col span={24}>
                <Collapse
                    activeKey={activeKey}
                    onChange={(keys) => setActiveKey(keys)}
                >
                    <Panel header={t('changeHistory.changeHistory')} key="1">
                        {isLoading && (
                            <Row justify="center" style={{ padding: '20px 0' }}>
                                <Spin />
                            </Row>
                        )}
                        {!isLoading && logs.length === 0 && (
                            <Empty
                                description={t('changeHistory.noHistoryFound')}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        )}
                        {!isLoading && logs.length > 0 && (
                            <Timeline>
                                {logs.map((log) => {
                                    const groupedChanges = groupChangesBySection(log.changes);
                                    const hasChanges = log.changes && log.changes.length > 0;

                                    return (
                                        <Timeline.Item key={log.id} color={getActionColor(log.action)}>
                                            <div style={{ marginLeft: 8 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <Text strong>{getActionLabel(log.action, t)}</Text>
                                                    {' '}{t('changeHistory.by')}{' '}
                                                    <Text strong>{log.username}</Text>
                                                    {' '}{t('changeHistory.at')}{' '}
                                                    <Text code>{getDateTimeDisplayText(log.changedAt)}</Text>
                                                    {log.identifierName && (
                                                        <Tag style={{ marginLeft: 8 }}>{log.identifierName}</Tag>
                                                    )}
                                                </div>

                                                {hasChanges && (
                                                    <div style={{ paddingLeft: 4 }}>
                                                        {Object.entries(groupedChanges).map(([sectionTitle, sectionChanges]) => (
                                                            <Card
                                                                key={sectionTitle}
                                                                size="small"
                                                                title={<Text type="secondary" style={{ fontSize: '0.85em' }}>{sectionTitle}</Text>}
                                                                style={{ marginBottom: 8, borderRadius: 6 }}
                                                                bodyStyle={{ padding: '8px 12px' }}
                                                            >
                                                                {sectionChanges.map((change, idx) => (
                                                                    <Row
                                                                        key={idx}
                                                                        gutter={[8, 4]}
                                                                        align="middle"
                                                                        style={{
                                                                            padding: '4px 0',
                                                                            borderBottom: idx < sectionChanges.length - 1 ? '1px solid #f5f5f5' : 'none'
                                                                        }}
                                                                    >
                                                                        <Col span={7}>
                                                                            <Text type="secondary" style={{ fontSize: '0.85em' }}>
                                                                                {getFieldDisplayName(change.field)}
                                                                            </Text>
                                                                        </Col>
                                                                        <Col span={8}>
                                                                            <Text
                                                                                type="danger"
                                                                                delete
                                                                                style={{ fontSize: '0.85em' }}
                                                                            >
                                                                                {renderChangeValue(change.old_value, change.field)}
                                                                            </Text>
                                                                        </Col>
                                                                        <Col span={1} style={{ textAlign: 'center' }}>
                                                                            <Text type="secondary" style={{ fontSize: '0.75em' }}>→</Text>
                                                                        </Col>
                                                                        <Col span={8}>
                                                                            <Text
                                                                                type="success"
                                                                                style={{ fontSize: '0.85em' }}
                                                                            >
                                                                                {renderChangeValue(change.new_value, change.field)}
                                                                            </Text>
                                                                        </Col>
                                                                    </Row>
                                                                ))}
                                                            </Card>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </Timeline.Item>
                                    );
                                })}
                            </Timeline>
                        )}
                    </Panel>
                </Collapse>
            </Col>
        </Row>
    );
}